import { DEVICE_FIELD_LIMITS } from './device-field-limits';

/**
 * EDUS-2544 — constraints for a device field that supports append/prepend.
 */
export interface AppendableFieldConfig {
  /** Max length of the *concatenated* result; overflow → device is skipped. */
  maxLength: number;
  /**
   * Optional pattern the concatenated result must satisfy. Reserved for future
   * appendable fields with content rules (e.g. `annotatedAssetId`); unused by
   * `notes`. When set, a result that fails the pattern skips the device.
   */
  pattern?: RegExp;
  /** Human-readable reason surfaced when {@link pattern} fails. */
  patternErrorMessage?: string;
}

/**
 * EDUS-2544 — the single source of truth for which device fields accept an
 * append/prepend {@link FieldOperator} in a bulk-update spec.
 *
 * The read-modify-write engine in the `run-update-batch` job step is
 * field-agnostic: it prefetches, concatenates, and length-checks based purely
 * on this map. To enable append/prepend for a new field you only need to
 *   1. add an entry here (with its `maxLength`, optional `pattern`),
 *   2. widen that field's type to `string | FieldOperator`, and
 *   3. offer Append/Prepend in the bulk-edit UI config.
 * No changes to the job step, saas-device-api, or the client are required.
 *
 * Note: only free-text fields written through the `chromeosdevices` PATCH
 * qualify. `orgUnitPath` never will (OU moves use a separate move API).
 */
export const APPENDABLE_FIELDS: Record<string, AppendableFieldConfig> = {
  notes: { maxLength: DEVICE_FIELD_LIMITS.notes },
};

/** True when `field` is allow-listed for append/prepend operators. */
export const isAppendableField = (field: string): boolean =>
  Object.hasOwn(APPENDABLE_FIELDS, field);
